import { useCallback, useEffect, useState } from 'react';
import { useRouter } from '@/i18n/routing';
import { GetLocationQuery } from '@/query';
import { useListingSearchStore } from '@/store';
import {
  buildSearchQuery,
  getQueryFromPath,
  prepareBookingParams,
} from '@/utils';
import { usePathname, useSearchParams } from 'next/navigation';
import { skipToken, useSuspenseQuery } from '@apollo/client';

export function useSearchFromUrl() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { setSearchFromQuery } = useListingSearchStore();
  const { data } = useSuspenseQuery(
    GetLocationQuery,
    searchParams.get('locationId')
      ? {
          variables: { locationSlug: searchParams.get('locationId') },
          errorPolicy: 'ignore',
        }
      : skipToken
  );

  useEffect(() => {
    function initSearchParams() {
      const query = getQueryFromPath(`${pathname}?${searchParams}`);

      let fullLocation = data?.location || {};

      setSearchFromQuery({
        ...query,
        locationName: fullLocation?.name,
        locationType: fullLocation?.type,
      });
    }
    initSearchParams();
  }, [pathname, searchParams, setSearchFromQuery, data?.location?.name]);
}

export function useSearchSubmit() {
  const {
    guests,
    date,
    location,
    pets,
    inexactStay,
    rooms,
    price,
    amenities,
    rating,
    tab,
  } = useListingSearchStore();
  const { push } = useRouter();
  const [redirecting, setRedirecting] = useState(false);

  const onSearch = useCallback(async () => {
    const params = prepareBookingParams({
      date,
      inexactStay,
      location,
      guests,
      pets,
      price,
      amenities,
      rooms,
      rating,
      tab,
    });

    const param = {
      location: params.location,
      date:
        params.dateRange.startDate && params.dateRange.endDate
          ? params.dateRange
          : undefined,
      inexactStay: params.dateFlexibility === 0 ? '0' : params.dateFlexibility,
      guests: params.guests,
      pets: params.pets,
      price: params.price,
      amenities: params.amenities,
      rooms: params.rooms,
      rating: params.rating,
      tab: params.tab,
    };

    setRedirecting(true);
    await push(
      `/listings${tab ? `/filters/${tab}` : ''}${buildSearchQuery(param)}`
    );
    setRedirecting(false);
  }, [push, setRedirecting, guests, date, location, pets, inexactStay]);

  return { onSearch, redirecting };
}
